import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Box, TableCell, Typography, Button, Skeleton } from "@mui/material";

import { CombinedSubscriptionPlan } from "../../../types/plans";
import { UserSubscription } from "../../../api/services/userSettings";

import { plansListStyles } from "./styles";
import PaymentSummaryModal from "./PaymentSummaryModal";

const getBillingTitle = (title: string | { ar: string; en: string }): string => {
  const titleMapper = {
    شهرى: "monthly",
    شهريا: "monthly",
    سنوى: "yearly",
    سنويا: "yearly",
    monthly: "monthly",
    yearly: "yearly",
  } as const;

  return (
    titleMapper[title as keyof typeof titleMapper] ||
    (typeof title === "string" ? title.toLowerCase() : title.en.toLowerCase())
  );
};

export const PlanDescriptionCell = ({
  combinedPlan: { id, title, description, imageUrl, isFree, is_subscribed, plans, svgImageUrl: SVGIcon },
}: {
  combinedPlan: CombinedSubscriptionPlan;
}) => {
  const { t } = useTranslation("plans", { keyPrefix: "page.plansPage" });
  const [searchParams] = useSearchParams();
  const billing = searchParams.get("billing") ?? "monthly";

  const plan = plans.find((plan) => {
    return getBillingTitle(plan.title) === billing?.toLowerCase();
  });

  return (
    <TableCell component="td" scope="row" key={id} sx={plansListStyles.tablePlanItemCell}>
      <Box sx={plansListStyles.planContainer}>
        {/* <Box sx={plansListStyles.planStatusContainer}>
          {is_subscribed && <Typography sx={plansListStyles.planStatus(true)}>{t("subscribed")}</Typography>}
          {isFree && <Typography sx={plansListStyles.planStatus(false)}>{t("free")}</Typography>}
        </Box> */}
        {is_subscribed && <Typography sx={plansListStyles.planStatus(true)}>{t("subscribed")}</Typography>}
        <Box dangerouslySetInnerHTML={{ __html: SVGIcon }} sx={plansListStyles.planImage} />

        <Box sx={plansListStyles.planTitleContainer}>
          <Typography sx={plansListStyles.planTitle}>{title}</Typography>
          <Typography title={description} sx={plansListStyles.planDescription}>
            {description}
          </Typography>
        </Box>
        {plan?.price && (
          <Box sx={plansListStyles.planPriceContainer}>
            <Typography sx={plansListStyles.planPrice}>{t("planPrice", { price: plan?.price })}</Typography>
            <Typography sx={plansListStyles.planPriceDescription}>
              {billing === "monthly" ? t("perMonth") : t("perYear")}
            </Typography>
          </Box>
        )}
        {plan?.ginoToken && (
          <Box sx={plansListStyles.creditsContainer}>
            <Typography sx={plansListStyles.creditsTitle}>{t("numberOfCredits")}</Typography>
            <Typography sx={plansListStyles.creditsValue}>{plan?.ginoToken}</Typography>
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

export const PlanDescriptionCellLoader = () => {
  return (
    <TableCell component="td" scope="row" sx={plansListStyles.tablePlanItemCell}>
      <Box sx={plansListStyles.planContainer}>
        <Skeleton variant="rectangular" width={40} height={40} />
        <Box sx={plansListStyles.planTitleContainer}>
          <Skeleton variant="text" width={80} height={38} />
          <Skeleton variant="text" width={240} height={16} />
          <Skeleton variant="text" width={240} height={16} />
          <Skeleton variant="text" width={240} height={16} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
        <Box sx={plansListStyles.planPriceContainer}>
          <Skeleton variant="text" width={80} height={40} />
          <Skeleton variant="text" width={50} height={22} sx={{ mb: "6px" }} />
        </Box>
        <Box sx={plansListStyles.creditsContainer}>
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="text" width={50} height={18} />
        </Box>
      </Box>
    </TableCell>
  );
};

export const PlanActionCell = ({
  combinedPlan,
  userSubscription,
  isLoading,
}: {
  combinedPlan: CombinedSubscriptionPlan;
  userSubscription?: UserSubscription;
  isLoading: boolean;
}) => {
  const { t } = useTranslation("plans", { keyPrefix: "page.plansPage" });
  const [openPaymentSummaryModal, setOpenPaymentSummaryModal] = useState(false);
  const handleOpenPaymentSummaryModal = () => setOpenPaymentSummaryModal(true);
  const handleClosePaymentSummaryModal = () => setOpenPaymentSummaryModal(false);

  const [searchParams] = useSearchParams();
  const billing = searchParams.get("billing") ?? "monthly";

  const { id, title, plans, is_subscribed, isFree, upgradeOption } = combinedPlan;
  const plan = plans.find((plan) => {
    return getBillingTitle(plan.title) === billing?.toLowerCase();
  });

  if (combinedPlan.isFree)
    return <TableCell component="td" scope="row" key={id} sx={plansListStyles.tablePlanActionItemCell}></TableCell>;

  const isSubscribed = userSubscription?.subscription?.subscriptionPlan.id === plan?.id;
  // @ts-ignore
  const userParentPlanId = userSubscription?.subscription?.subscriptionPlan.subscriptionType.id as number;
  const parentPlanId = combinedPlan.id;
  const sameParentPlan = parentPlanId === userParentPlanId;
  const isUpgradable = parentPlanId > 2 && !sameParentPlan;

  return (
    <TableCell component="td" scope="row" key={id} sx={plansListStyles.tablePlanActionItemCell}>
      <Button
        fullWidth
        sx={plansListStyles.upgradeToPlanButton}
        onClick={handleOpenPaymentSummaryModal}
        disabled={isLoading || !userSubscription}
      >
        {/* {is_subscribed ? t("renewSubscription") : t("upgradeToPlan", { plan: title })} */}
        {isSubscribed || sameParentPlan ? t("renewSubscription") : t("upgradeToPlan", { plan: title })}
      </Button>
      <PaymentSummaryModal
        open={openPaymentSummaryModal}
        closeModal={handleClosePaymentSummaryModal}
        durationInMonths={plan?.durationMonths!}
        planId={plan?.id!}
        planTitle={title}
        frequency={billing}
        price={plan?.price!}
        isSubscribed={is_subscribed}
        userSubscription={userSubscription}
        isUpgradable={isUpgradable}
      />
    </TableCell>
  );
};

export const PlanActionCellLoader = () => {
  return (
    <TableCell component="td" scope="row" sx={plansListStyles.tablePlanActionItemCell}>
      <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "40px" }} />
    </TableCell>
  );
};
